<template>
  <VdPage class="segments-list-page" title="Segments" :subtitle="`(${segments.length})`" :loading="loading">
    <template #actions>
      <InputText
        ref="searchInputRef"
        v-model="searchTerm"
        class="search-input"
        placeholder="Looking for something..."
        @focusout="forceFocusOnSearchInput"
      />
      <vd-button
        v-if="inWIPVersion && canWriteSegments"
        class="new-segment-button"
        type="secondary"
        skin="blue"
        icon-left="fa:add"
        @click="onNewSegment"
      >
        New segment
      </vd-button>
    </template>

    <keep-alive v-if="finished && filteredSegments.length === 0">
      <vd-empty :title="searchTerm ? 'not found' : 'no segments'">
        <div v-if="searchTerm">
          <p>Your search for a Segment has no results</p>
        </div>
        <div v-else>
          <p>Sorry, there is no data.</p>
          <p v-if="inWIPVersion">Refresh the page or try the following option:</p>
        </div>
        <template v-if="!searchTerm && inWIPVersion && canWriteSegments" #actions>
          <vd-button type="secondary" skin="blue" icon-left="fa:add" @click="onNewSegment">New segment</vd-button>
        </template>
      </vd-empty>
    </keep-alive>

    <el-table v-else-if="finished" :data="filteredSegments" style="width: 100%" class="segments-table">
      <el-table-column prop="name" sortable label="Name" width="280">
        <template #default="scope">
          <div class="segment-name">
            <h4 class="name-link" @click="(e) => goToPreviewSegment(e, scope.row.id)">
              {{ scope.row.name || "(unnamed)" }}
            </h4>
            <el-tag v-if="scope.row.isSystem" size="small" type="info">system</el-tag>
          </div>
        </template>
      </el-table-column>
      <el-table-column label="Segment ID">
        <template #default="scope">
          <span class="segment-id">{{ scope.row.id }}</span>
          <CopyButton :id="scope.row.id" @click.stop />
        </template>
      </el-table-column>
      <el-table-column prop="description" label="Description" />

      <!-- Segment menu actions column -->
      <el-table-column v-if="inWIPVersion && canWriteSegments" fixed="right" label="" width="60">
        <template #default="scope">
          <vd-dropdown v-if="!scope.row.isSystem">
            <vd-dropdown-item @click="onSegmentEdit(scope.row.id)">Edit</vd-dropdown-item>
            <vd-dropdown-item @click="onSegmentDuplicate(scope.row.id)">Duplicate</vd-dropdown-item>
            <vd-dropdown-item dangerous @click="onSegmentDelete(scope.row.id)">Delete</vd-dropdown-item>
          </vd-dropdown>
        </template>
      </el-table-column>
    </el-table>

    <!-- warning delete dialog -->
    <vd-dialog
      :visible="displayDeleteWarning"
      title="This segment can't be deleted"
      type="error"
      @close="closeDeleteWarning"
    >
      <span>You can't delete this segment as it is used</span>
      <strong v-if="deleteWarningDetails.configurationRules.length">
        in {{ deleteWarningDetails.configurationRules.length }}
        {{ pluralize("configuration rule", deleteWarningDetails.configurationRules.length) }}
      </strong>
      <span v-if="deleteWarningDetails.abtests.length && deleteWarningDetails.configurationRules.length">
        and&nbsp;
      </span>
      <span v-if="deleteWarningDetails.abtests.length">
        <strong>in the following A/B Tests:</strong>
        <ul class="delete-warning-list">
          <li v-for="abtest in deleteWarningDetails.abtests" :key="abtest.id">
            {{ abtest.name }}
          </li>
        </ul>
      </span>
      <template #footer>
        <vd-button type="secondary" skin="blue" @click="closeDeleteWarning">I understand</vd-button>
      </template>
    </vd-dialog>
  </VdPage>
</template>

<script setup lang="ts">
import CopyButton from "@/components/CopyButton/CopyButton.vue";
import { VdPage } from "@voodoo.io/voodoo-ui";
import InputText from "primevue/inputtext";
import { computed, reactive, ref } from "vue";
import { useRouter } from "vue-router";

import { pluralize } from "@/filters";
import {
  DeleteSegmentMutationResponse,
  queryNames,
  useSegmentsListPageDeleteMutation,
  useSegmentsListPageQuery,
} from "@/graphql";
import { useMutationResponse } from "@/graphql/utils";
import useMessages from "@/hooks/messages";
import useSearchFilter from "@/hooks/search-filter";
import usePermissions from "@/hooks/use-permissions";
import links from "@/router/links";

const props = defineProps({
  gameId: {
    type: String,
    required: true,
  },
  versionId: {
    type: String,
    required: true,
  },
});
const router = useRouter();
const { canWriteSegments } = usePermissions();
const { confirm } = useMessages();

const searchInputRef = ref();

const forceFocusOnSearchInput = () => {
  if (searchInputRef.value) {
    searchInputRef.value.$el.focus();
  }
};

/**
 * Fetch game's segments
 */
const { result, loading: querySegmentsLoading } = useSegmentsListPageQuery(
  { gameId: props.gameId, versionId: props.versionId },
  { fetchPolicy: "network-only" }
);

const segments = computed(() => result.value?.game?.version?.segments || []);

const inWIPVersion = computed(() => result.value?.game?.version?.isWip);

const finished = computed(() => !querySegmentsLoading.value);

/**
 * Filter the segments from the searchTerm
 */
const { searchTerm, filteredItems: filteredSegments } = useSearchFilter(segments, ["name", "description"]);

/**
 * Some links
 */
const onNewSegment = () => {
  router.push({ name: links.games.segments.new, params: { gameId: props.gameId, versionId: props.versionId } });
};

const getPreviewRoute = (segmentId: string) => ({
  name: links.games.segments.preview,
  params: {
    gameId: props.gameId,
    versionId: props.versionId,
    segmentId,
  },
});

const goToPreviewSegment = (event: KeyboardEvent, segmentId: string) => {
  const previewRoute = getPreviewRoute(segmentId);

  if (event.metaKey || event.ctrlKey) {
    const routeData = router.resolve(previewRoute);
    window.open(routeData.href, "_blank");
  } else {
    router.push(previewRoute);
  }
};

/**
 * Go to edit form
 */
const onSegmentEdit = (segmentId: string) => {
  router.push({
    name: links.games.segments.edit,
    params: {
      gameId: props.gameId,
      versionId: props.versionId,
      segmentId,
    },
  });
};

/**
 * Go to duplicate form
 */
const onSegmentDuplicate = (segmentId: string) => {
  router.push({
    name: links.games.segments.duplicate,
    params: {
      gameId: props.gameId,
      versionId: props.versionId,
      segmentId,
    },
  });
};

/**
 *  Delete a segment
 */
const { mutate: deleteSegment, loading: deleteSegmentLoading } = useSegmentsListPageDeleteMutation({
  refetchQueries: [queryNames.SegmentsListPage],
});

/**
 * If the segment is used in an A/B Test cohort or a configuration rule,
 * we cannot delete it without corrupting the data integrity.
 * We therefore display an information dialog that explain to the user
 * that he has to remove the segment from those resource before continuing.
 */
const deleteWarningDetails = reactive<{
  abtests: NonNullable<DeleteSegmentMutationResponse["usedInAbtests"]>;
  configurationRules: NonNullable<DeleteSegmentMutationResponse["usedInConfigurationRules"]>;
}>({
  abtests: [],
  configurationRules: [],
});

const displayDeleteWarning = ref(false);

const closeDeleteWarning = () => {
  displayDeleteWarning.value = false;
};

const onSegmentDelete = async (segmentId: string) => {
  const confirmed = await confirm("Do you really want to delete this segment?");
  if (confirmed) {
    const response = await deleteSegment({ gameId: props.gameId, versionId: props.versionId, segmentId });
    const deleteResult = useMutationResponse(response, (resp) => resp?.deleteSegment);
    if (deleteResult?.isCurrentlyUsed) {
      deleteWarningDetails.abtests = deleteResult?.usedInAbtests ?? [];
      deleteWarningDetails.configurationRules = deleteResult?.usedInConfigurationRules ?? [];
      displayDeleteWarning.value = true;
    }
  }
};

/**
 * The loading status depends on three different loader
 */
const loading = computed(() => querySegmentsLoading.value || deleteSegmentLoading.value);
</script>

<style lang="scss" scoped>
.segments-list-page {
  .segments-table {
    @extend %card;
    // border-bottom-width: 0;

    .name-link {
      color: var(--vd-base-primary-color);
      cursor: pointer;
      padding: size("ML") size("M");
      word-break: break-word;
    }

    .segment-name {
      display: flex;
      align-items: center;
    }
  }

  .delete-warning-list {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    max-height: 300px;
  }

  .segment-id {
    font-size: 0.75rem;
    color: var(--vd-color-grey-light-1);
    margin-right: 0.25rem;
  }
}
</style>
